import {
  parse_skeleton_descriptor,
  type SkeletonDescriptor
} from '../skeleton/SkeletonDescriptor'
import { CUSTOM_SKELETONS_STORE, get_db } from './db'

/**
 * IndexedDB persistence for user-authored custom skeletons (the output of the
 * Build Skeleton mode). Mirrors the failure-soft pattern of `UserClipStorage`:
 * any IDB error (private browsing, quota, missing API) is caught and logged,
 * with the in-memory app continuing to function.
 *
 * Each entry is a single SkeletonDescriptor keyed by its `id` (uuid).
 */

export class CustomSkeletonStorage {
  static async list (): Promise<SkeletonDescriptor[]> {
    try {
      const db = await get_db()
      const all = await db.getAll(CUSTOM_SKELETONS_STORE) as unknown[]
      const valid: SkeletonDescriptor[] = []
      for (const raw of all) {
        try {
          valid.push(parse_skeleton_descriptor(raw))
        } catch (err) {
          console.warn('CustomSkeletonStorage.list: dropping invalid entry:', err)
        }
      }
      // Sort by name for stable UI ordering.
      valid.sort((a, b) => a.name.localeCompare(b.name))
      return valid
    } catch (err) {
      console.warn('CustomSkeletonStorage.list failed:', err)
      return []
    }
  }

  static async get (id: string): Promise<SkeletonDescriptor | null> {
    try {
      const db = await get_db()
      const raw = await db.get(CUSTOM_SKELETONS_STORE, id) as unknown
      if (raw === undefined) return null
      return parse_skeleton_descriptor(raw)
    } catch (err) {
      console.warn(`CustomSkeletonStorage.get(${id}) failed:`, err)
      return null
    }
  }

  static async put (descriptor: SkeletonDescriptor): Promise<boolean> {
    try {
      const db = await get_db()
      const updated: SkeletonDescriptor = { ...descriptor, updated_at: new Date().toISOString() }
      await db.put(CUSTOM_SKELETONS_STORE, updated)
      return true
    } catch (err) {
      console.warn(`CustomSkeletonStorage.put(${descriptor.id}) failed:`, err)
      return false
    }
  }

  static async delete (id: string): Promise<boolean> {
    try {
      const db = await get_db()
      await db.delete(CUSTOM_SKELETONS_STORE, id)
      return true
    } catch (err) {
      console.warn(`CustomSkeletonStorage.delete(${id}) failed:`, err)
      return false
    }
  }

  static async clear_all (): Promise<void> {
    try {
      const db = await get_db()
      await db.clear(CUSTOM_SKELETONS_STORE)
    } catch (err) {
      console.warn('CustomSkeletonStorage.clear_all failed:', err)
    }
  }
}
